import { el, uniqueId } from "../dom.ts";
import { icon } from "./icon.ts";

/**
 * Labelled text field with help and error messaging.
 *
 * Wiring that the UI UX Pro Max forms guidance requires and that is easy to get
 * subtly wrong by hand, so it is done once here:
 *
 * - a real `<label for>`, never a placeholder standing in for a label;
 * - help and error text referenced by `aria-describedby`, so a screen reader
 *   reads them as part of the control rather than as stray paragraphs;
 * - `aria-invalid` alongside the visual error state, because colour alone is
 *   not a signal;
 * - the error node carries `role="alert"` so a validation failure is announced.
 *
 * There is deliberately **no money variant of this control.**
 * PHASE_01_PRODUCTION_ARCHITECTURE.md §17: "The keypad emits digits with an
 * explicit decimal decision; locale strings are never parsed from a text field."
 * Amounts are entered through the keypad in `amount.ts`.
 */

export interface FieldOptions {
  readonly label: string;
  readonly value?: string;
  readonly type?: "text" | "email" | "tel" | "url" | "search";
  readonly placeholder?: string;
  readonly help?: string;
  readonly error?: string;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly inputMode?: "text" | "numeric" | "decimal" | "tel" | "email" | "url";
  readonly autocomplete?: string;
  readonly onInput?: (value: string) => void;
}

export interface Field {
  readonly root: HTMLElement;
  readonly input: HTMLInputElement;
  /** Set or clear the error, keeping every aria relationship consistent. */
  setError(message: string | undefined): void;
}

export function field(options: FieldOptions): Field {
  const id = uniqueId("nura-field");
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;

  const input = el("input", {
    class: "nura-field__control",
    attrs: {
      id,
      type: options.type ?? "text",
      value: options.value ?? "",
      placeholder: options.placeholder,
      required: options.required === true,
      disabled: options.disabled === true,
      readonly: options.readOnly === true,
      inputmode: options.inputMode,
      autocomplete: options.autocomplete,
    },
  });

  const help =
    options.help === undefined
      ? undefined
      : el("p", { class: "nura-field__help", text: options.help, attrs: { id: helpId } });

  const error = el("p", {
    class: "nura-field__error",
    attrs: { id: errorId, role: "alert", hidden: true },
  });

  const label = el("label", { class: "nura-field__label", attrs: { for: id } }, [
    options.label,
    options.required === true
      ? el("span", { class: "nura-field__required", text: "*", attrs: { "aria-hidden": true } })
      : undefined,
    options.required === true
      ? el("span", { class: "nura-sr-only", text: " (required)" })
      : undefined,
  ]);

  const root = el("div", { class: "nura-field" }, [label, input, help, error]);

  function setError(message: string | undefined): void {
    const described = [
      help === undefined ? undefined : helpId,
      message === undefined ? undefined : errorId,
    ]
      .filter((value): value is string => value !== undefined)
      .join(" ");

    if (described === "") input.removeAttribute("aria-describedby");
    else input.setAttribute("aria-describedby", described);

    if (message === undefined) {
      error.hidden = true;
      error.replaceChildren();
      input.removeAttribute("aria-invalid");
      return;
    }

    error.hidden = false;
    error.replaceChildren(
      el("span", { class: "nura-icon-slot", staticHtml: icon("warning", 16) }),
      el("span", { text: message }),
    );
    input.setAttribute("aria-invalid", "true");
  }

  setError(options.error);

  if (options.onInput !== undefined) {
    const handler = options.onInput;
    input.addEventListener("input", () => {
      handler(input.value);
    });
  }

  return { root, input, setError };
}
